import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { CustomModal, Button, TableList, Page, Spinner, PageErrorState, PageContentHeader } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useEventReporter } from '~/event-reporter';
import { useIntegrations } from '~/hooks/reactQuery/queries/useIntegrations';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { modalSize } from '~/infra/modals';
import { sendErrReport } from '~/infra/utils';
import { integrationModel } from '~/models';
import { useStore, useStoreActions } from '~/store/store-context';
import { getAccountingDetails } from '../accounting.utils';
import styles from './MapAccountsModal.module.scss';
import { columnDefs, makeHeaderItems } from './MapAccountsModal.utils';
import { MapAccountsRow } from './MapAccountsRow';
export const GetMapAccountsModalTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            MapAccountsModalTID: {
                Modal: (testid)=>testid ? "".concat(testid, "-map-accounts-modal") : '',
                Close: (testid)=>testid ? "".concat(testid, "-map-accounts-close") : '',
                BankRow: (testid)=>testid ? "".concat(testid, "-map-bank-account-row") : '',
                ExpenseRow: (testid)=>testid ? "".concat(testid, "-map-expense-account-row") : '',
                ClearingRow: (testid)=>testid ? "".concat(testid, "-map-clearing-account-row") : '',
                Loading: 'map-accounts-loading',
                ErrorState: 'map-accounts-error-state'
            }
        }
    });
export function MapAccountsModal(param) {
    let { dismissModal, accounting } = param;
    const { t: accountingTexts } = useLocaleKeys('integrations.accounting');
    const { t: mapTexts } = useLocaleKeys('integrations.mapAccountsModal');
    const { api, logBI } = useStoreActions();
    const { integrations, refresh: refreshIntegrations } = useIntegrations({
        enabled: false
    });
    const { reportToHeap } = useEventReporter();
    const accountingIntegration = integrationModel.accountingIntegration(integrations, accounting);
    const [modalState, setModalState] = useState('loading');
    const [accountingAccounts, setAccountingAccounts] = useState();
    const userInfo = useStore((state)=>state.userInfo);
    const { testIdPrefix, biCodes } = getAccountingDetails(accounting);
    const { MapAccountsModalTID } = useMemo(()=>GetMapAccountsModalTID(), []);
    async function fetchAccounts() {
        setModalState('loading');
        try {
            const data = await api.accounting.getAccounts({
                accounting
            });
            setAccountingAccounts(data);
            setModalState('valid');
        } catch (err) {
            setModalState('error');
        }
    }
    async function onLink(linkType, account) {
        const isPaymentAccount = linkType !== 'expense';
        const biEventCode = isPaymentAccount ? biCodes.linkAccountToPaymentAccount : biCodes.linkAccount;
        logBI(biEventCode, {
            vendorId: userInfo === null || userInfo === void 0 ? void 0 : userInfo.businessId(),
            userId: userInfo === null || userInfo === void 0 ? void 0 : userInfo.id()
        });
        const heapEventName = isPaymentAccount ? "".concat(testIdPrefix, "-select-payments-account") : "".concat(testIdPrefix, "-select-anchor-fees-account");
        reportToHeap('integrations', 'click', heapEventName);
        try {
            await api.accounting.linkAccount({
                accounting,
                data: {
                    linkType,
                    integratedId: account === null || account === void 0 ? void 0 : account.id
                }
            });
            await refreshIntegrations({
                throwOnError: false
            });
        } catch (error) {
            sendErrReport('Failed to link service template', error);
            throw error;
        }
    }
    useOnMount(()=>{
        fetchAccounts();
    });
    return /*#__PURE__*/ _jsx(CustomModal, _object_spread_props(_object_spread({
        bi: {
            closeButton: {
                name: 'map_accounts_modal_close'
            }
        },
        testId: MapAccountsModalTID.Modal(testIdPrefix),
        onClose: dismissModal
    }, modalSize.large), {
        footer: /*#__PURE__*/ _jsx("div", {
            className: styles.footer,
            children: /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'close_map_accounts_modal'
                },
                onClick: dismissModal,
                testId: MapAccountsModalTID.Close(testIdPrefix),
                children: mapTexts.done()
            })
        }),
        children: /*#__PURE__*/ _jsx(TableList.Provider, {
            columnDefs: columnDefs,
            children: /*#__PURE__*/ _jsxs(Page, {
                height: "100%",
                header: mapTexts.accountsHeader({
                    accountingName: getAccountingDetails(accounting).fullName
                }),
                subheader: accountingTexts.mapAccountsSubheader(),
                minWidth: "880px",
                skin: "secondary",
                children: [
                    modalState === 'loading' && /*#__PURE__*/ _jsx(LoadingState, {}),
                    modalState === 'error' && /*#__PURE__*/ _jsx(ErrorState, {
                        onRefresh: fetchAccounts
                    }),
                    modalState === 'valid' && accountingAccounts && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(PageContentHeader, {
                                children: /*#__PURE__*/ _jsx(TableList.Header, {
                                    items: makeHeaderItems(mapTexts)
                                })
                            }),
                            /*#__PURE__*/ _jsx(MapAccountsRow, {
                                anchorAccount: mapTexts.anchorAccountBank(),
                                tooltip: accountingTexts.mapAccountsBankTooltip(),
                                selectedAccountId: accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.getBankAccountId(),
                                accounts: accountingAccounts.bank,
                                onLink: (account)=>onLink('bank', account),
                                testId: MapAccountsModalTID.BankRow(testIdPrefix),
                                noOptionsText: mapTexts.noOptionsTextBank(),
                                accounting: accounting
                            }),
                            /*#__PURE__*/ _jsx(MapAccountsRow, {
                                anchorAccount: mapTexts.anchorAccountExpense(),
                                tooltip: accountingTexts.mapAccountsExpenseTooltip(),
                                selectedAccountId: accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.getExpenseAccountId(),
                                accounts: accountingAccounts.expense,
                                onLink: (account)=>onLink('expense', account),
                                testId: MapAccountsModalTID.ExpenseRow(testIdPrefix),
                                accounting: accounting
                            }),
                            /*#__PURE__*/ _jsx(MapAccountsRow, {
                                anchorAccount: mapTexts.anchorAccountClearing(),
                                tooltip: accountingTexts.mapAccountsClearingTooltip(),
                                selectedAccountId: accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.getClearingAccountId(),
                                accounts: accountingAccounts.clearing,
                                onLink: (account)=>onLink('clearing', account),
                                testId: MapAccountsModalTID.ClearingRow(testIdPrefix),
                                accounting: accounting
                            })
                        ]
                    })
                ]
            })
        })
    }));
}
const LoadingState = ()=>{
    const { MapAccountsModalTID } = useMemo(()=>GetMapAccountsModalTID(), []);
    return /*#__PURE__*/ _jsx("div", {
        className: styles.loading,
        children: /*#__PURE__*/ _jsx(Spinner, {
            size: 40,
            testId: MapAccountsModalTID.Loading()
        })
    });
};
const ErrorState = (param)=>{
    let { onRefresh } = param;
    const { t: accountingTexts } = useLocaleKeys('integrations.accounting');
    const { MapAccountsModalTID } = useMemo(()=>GetMapAccountsModalTID(), []);
    return /*#__PURE__*/ _jsx(PageErrorState, {
        testId: MapAccountsModalTID.ErrorState(),
        message: accountingTexts.mapAccountsError(),
        onRefresh: onRefresh,
        skin: "secondary"
    });
};
